import { LoadingIndicator } from '../common/components/loading-indicator';
import { css, EmptyState, Flex, HStack, styled, VStack, IconButton, Text } from '@cad-arc';
import { ReactNode } from 'react';
import { ErrorBoundary } from '../monitoring/error-boundary';
import { TOTAL_NON_SAVED_SEARCHES_HEIGHT_PX } from './constants';
import { FailedToLoadError } from '../common/components/failed-to-load-error';
import { useApplicationType } from '../common/hooks/use-application-type';

interface SavedSearchesProps {
    hasSavedSearches: boolean;
    isNoSavedSearches: boolean;
    isLoading: boolean;
    isError: boolean;
    children: ReactNode;
    onClose?: () => void;
    hideTitle?: boolean;
}

const SavedSearchesWrapper = styled(VStack)<{ isFirstResponder: boolean }>`
    background-color: var(--arc-colors-surface-canvas);
    overflow-y: auto;
    padding: 24px 56px;
    ${(props) =>
        props.isFirstResponder
            ? css`
                  width: 100%;
                  height: 100%;
              `
            : css`
                  height: calc(100% - ${TOTAL_NON_SAVED_SEARCHES_HEIGHT_PX}px);
                  position: absolute;
                  top: 0;
                  bottom: 0;
                  right: 0;
                  left: 0;
              `}}
`;

export function SavedSearches({
    hasSavedSearches,
    isNoSavedSearches,
    isLoading,
    isError,
    onClose,
    children,
    hideTitle,
}: SavedSearchesProps) {
    const { isFirstResponder } = useApplicationType();

    return (
        <ErrorBoundary name="SAVED_SEARCHES">
            <SavedSearchesWrapper align="left" isFirstResponder={isFirstResponder}>
                <HStack justifyContent="space-between">
                    {!hideTitle && <Text variant="headingMd">Saved Searches</Text>}
                    {onClose && (
                        <IconButton
                            variant="ghost"
                            icon="CloseX"
                            aria-label="close saved searches"
                            onClick={() => onClose()}
                        />
                    )}
                </HStack>
                {isLoading && <LoadingIndicator />}
                {isError && <FailedToLoadError noun="saved searches" />}
                {!isError && !isLoading && isNoSavedSearches && <NoSavedSearches />}
                {hasSavedSearches && children}
            </SavedSearchesWrapper>
        </ErrorBoundary>
    );
}

function NoSavedSearches() {
    return (
        <Flex justifyContent="center" alignItems="center" h="100%" w="100%">
            <EmptyState
                title="No saved searches found"
                subtitle="Save searches when viewing results"
                visual="SavedSearchInactive"
            />
        </Flex>
    );
}
